import { type FC, useEffect, useMemo, useRef } from 'react'

import { type ChatMessageStatus } from '@api'

import { IconLoading } from '@/assets/icons/icon-loading'
import { DataTable } from '@/components/ui/table/controlled-pagination/DataTable'

import { useInViewPort } from '../../../hooks/useInViewport'
import { getPpvOverviewColumns } from './constants'
import { usePpvOverview } from './usePpvOverview'

interface Props {
  onClose: () => void
  status?: ChatMessageStatus
  isOpen: boolean
}

export const OverviewTable: FC<Props> = ({ status, isOpen, onClose }) => {
  const { ppvs, hasMore, fetchNextPage, isLoading, isFetchingNextPage } =
    usePpvOverview({
      status,
      enabled: isOpen,
    })
  const bottomRef = useRef<HTMLDivElement>(null)
  const inBottom = useInViewPort(bottomRef, { threshold: 0.5 })

  useEffect(() => {
    if (inBottom && hasMore && !isLoading && !isFetchingNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasMore, inBottom, isFetchingNextPage, isLoading])

  const columns = useMemo(() => getPpvOverviewColumns({ onClose }), [onClose])

  if (isLoading && !ppvs.length) {
    return (
      <div className="flex h-[300px] items-center justify-center">
        <IconLoading className="h-8 w-8 animate-spin" />
      </div>
    )
  }

  return (
    <div>
      <DataTable
        columns={columns}
        tbodyMinHeight={false}
        data={ppvs}
        pageCount={9}
        stickyHeaderSize="300px"
        className="px-0"
        emptyClassName="[&>svg]:h-[140px] [&>svg]:w-[140px]"
        cellClassName="px-2 py-3"
        footerRow={
          hasMore && (
            <div
              className="col-span-full flex h-20 items-center justify-center"
              ref={bottomRef}
            >
              <IconLoading className="ml-0 mr-0 h-8 w-8 animate-spin" />
            </div>
          )
        }
      />
    </div>
  )
}
